import { z } from "zod";
import { brandAccessSchema, brandOptionSchema } from "./access.js";
/**
 * API keys for the MCP server (DESIGN.md "AI apps", agreed 2026-10-11), the
 * way Stripe's restricted keys and GitHub's fine-grained tokens work: a key
 * acts as the owner or admin who made it, narrowed to its own brands and
 * switches. Reading is never a switch; a key reads everything on its brands.
 *
 * The Rust API answers in exactly these shapes (`server/crates/api/src/modules/
 * api_keys`): `GET /api-keys`, `POST /api-keys`, `PATCH /api-keys/{keyId}`,
 * `POST /api-keys/{keyId}/roll`, `DELETE /api-keys/{keyId}`.
 */
/** What a key may change. Adding brands is not something a key does. */
export const keyAbilitiesSchema = z.object({
    questions: z.boolean(),
    editBrands: z.boolean(),
    pauseTracking: z.boolean(),
});
export const KEY_ABILITY_KEYS = ["questions", "editBrands", "pauseTracking"];
/** A new key starts here, and the most an owner can raise it to. */
export const DEFAULT_REQUESTS_PER_MINUTE = 60;
export const MOST_REQUESTS_PER_MINUTE = 600;
/** How long a new key lives, GitHub's choices. Fixed once it is made. */
export const keyExpirySchema = z.enum(["30d", "90d", "1y", "never"]);
/** The app a key is made for: its logo, and the setup a roll ends on. */
export const keyClientSchema = z.enum(["claude_code", "cursor", "other"]);
/**
 * The app a connection is for, its logo: a key's app, or one that signed in
 * with Crux (`oauth.ts`). Claude and ChatGPT only ever sign in.
 */
export const connectionClientSchema = z.enum([
    "claude_code",
    "cursor",
    "other",
    "claude",
    "chatgpt",
]);
/** A pasted key, or an app that signed in, which has no secret to roll. */
export const connectionKindSchema = z.enum(["key", "oauth"]);
export const apiKeySchema = z.object({
    id: z.string(),
    name: z.string(),
    kind: connectionKindSchema,
    lastFour: z.string(),
    client: connectionClientSchema,
    brands: brandAccessSchema,
    abilities: keyAbilitiesSchema,
    requestsPerMinute: z.number().int(),
    expiresAt: z.string().nullable(),
    lastUsedAt: z.string().nullable(),
    createdAt: z.string(),
    createdBy: z.string(),
    /** Who made it, so the page can tell the reader's own from everyone else's. */
    createdById: z.string(),
});
export const apiKeysListSchema = z.object({
    keys: z.array(apiKeySchema),
    brands: z.array(brandOptionSchema),
    /** Where an MCP app connects. */
    mcpUrl: z.string(),
});
export const apiKeyInputSchema = z.object({
    name: z.string().trim().min(1).max(80),
    brands: brandAccessSchema,
    abilities: keyAbilitiesSchema,
    requestsPerMinute: z.number().int().min(1).max(MOST_REQUESTS_PER_MINUTE),
});
/** The secret is in this answer and nowhere else: only its hash is stored. */
export const createdApiKeySchema = z.object({
    key: apiKeySchema,
    secret: z.string(),
});
//# sourceMappingURL=api-keys.js.map